import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UserImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Domain wrapper around the generic `ImportSheet` for users. The body
 * spells out the required vs optional columns so users know what
 * shape to upload without consulting docs.
 *
 * `Employee Cost` is informational in the export and IGNORED on import
 * — cost periods are versioned and change through the dedicated
 * /api/user-employee-costs flow.
 */
export function UserImportSheet({ open, onOpenChange }: UserImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import employees"
      description="Upload an .xlsx or .csv."
      onImport={async (file) => {
        const res = await usersApi.importFile(file);
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await usersApi.exportTemplate();
          triggerDownload(blob, filename ?? 'employees-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
